import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../components/LucideIcon";
import axios from "axios";
import { hostname, parseError } from "../../utils";
import TemplateBuilderDialog, { CampaignTemplate } from "./dialogs/TemplateBuilderDialog";

interface MarketingCampaignTemplatesProps {
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  onApplyTemplate: (templateId: string) => void;
}

const MarketingCampaignTemplates: React.FC<MarketingCampaignTemplatesProps> = ({
  companyId,
  getToken,
  eventListener,
  openDialog,
  onApplyTemplate,
}) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CampaignTemplate | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTemplate, setMenuTemplate] = useState<CampaignTemplate | null>(null);

  const openRowMenu = (
    event: React.MouseEvent<HTMLElement>,
    template: CampaignTemplate
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTemplate(template);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuTemplate(null);
  };

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${hostname}/marketing-campaign-templates`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data?.items || []);
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setLoading(false);
    }
  }, [companyId, getToken, eventListener]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (template: CampaignTemplate) => {
    setEditing(template);
    setFormOpen(true);
  };

  const handleDelete = (template: CampaignTemplate) => {
    openDialog(
      "Delete Template",
      `Are you sure you want to delete template "${template.name}"?`,
      "Delete",
      async () => {
        try {
          const token = await getToken();
          await axios.delete(
            `${hostname}/marketing-campaign-templates/${template.id}`,
            {
              params: { companyId },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          loadTemplates();
        } catch (err) {
          eventListener({ type: "SET_ERROR", error: parseError(err) });
        }
      }
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Typography variant="body2" color="text.secondary">
          Save a reusable email style and settings. Drop <code>{"{{content}}"}</code> where the per-campaign content should appear.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={loadTemplates} disabled={loading}>
                <LucideIcon name="RefreshCw" />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<LucideIcon name="Plus" size={18} />} onClick={handleNew}>
            New Template
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No templates yet. Click "New Template" to build a reusable email
          style.
        </Alert>
      ) : (
        <Paper sx={{ mt: 2 }}>
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Default List</TableCell>
                  <TableCell>Default Sender</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {templates.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  onClick={() => handleEdit(t)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{t.name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {t.description || "—"}
                  </TableCell>
                  <TableCell>{t.subject || "—"}</TableCell>
                  <TableCell>{t.smartList?.name || "—"}</TableCell>
                  <TableCell>
                    {t.senderProfile?.name || t.senderProfile?.email || "—"}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={(e) => openRowMenu(e, t)}>
                      <LucideIcon name="EllipsisVertical" size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuTemplate) onApplyTemplate(menuTemplate.id);
            closeRowMenu();
          }}
        >
          Use template
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTemplate) handleEdit(menuTemplate);
            closeRowMenu();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTemplate) handleDelete(menuTemplate);
            closeRowMenu();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <TemplateBuilderDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        companyId={companyId}
        getToken={getToken}
        template={editing}
        onSaved={() => {
          setFormOpen(false);
          loadTemplates();
        }}
        onError={(err) => eventListener({ type: "SET_ERROR", error: err })}
      />
    </Box>
  );
};

export default MarketingCampaignTemplates;
